class ShaderGradientBg extends HTMLElement {
  static get observedAttributes() {
    return ['gradient-color'];
  }

  /* Returns true when the device is not capable of running a smooth WebGL
     render loop: low CPU core count or low reported RAM.
     The shader is decorative — skipping it on weak hardware is a net win. */
  static isLowEndDevice() {
    // Low hardware concurrency (<= 2 threads) signals a weak CPU
    const cores = navigator.hardwareConcurrency;
    if (cores && cores <= 2) return true;
    // Low device memory (<= 1 GB) — available in Chromium browsers
    const ram = navigator.deviceMemory;
    if (ram && ram <= 1) return true;
    return false;
  }

  connectedCallback() {
    const gradientColor = this.getAttribute('gradient-color');
    if (ShaderGradientBg.isLowEndDevice() || !this.initWebGL()) {
      if (gradientColor) {
        this.style.background = gradientColor;
      }
      const canvas = this.querySelector('.shader-canvas');
      if (canvas) canvas.style.display = 'none';
      return;
    }
    this.style.background = '';
    this.setupVisibilityObserver();
  }

  /* The WebGL render loop otherwise runs indefinitely once mounted, burning
     GPU/CPU on shader instances scrolled far out of view (e.g. a card grid's
     background, or richtext's optional shader below the fold). Pause it
     while fully out of viewport and resume when it scrolls back in. No-op
     under reduced motion, which already renders a single static frame
     instead of a loop. */
  setupVisibilityObserver() {
    if (this.reducedMotion || !('IntersectionObserver' in window)) return;

    this.visibilityObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          if (this.gl && this.renderLoop && !this.renderFrameId) {
            this.renderFrameId = requestAnimationFrame(this.renderLoop);
          }
        } else if (this.renderFrameId) {
          cancelAnimationFrame(this.renderFrameId);
          this.renderFrameId = null;
        }
      });
    }, { threshold: 0 });

    this.visibilityObserver.observe(this);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'gradient-color' && oldValue !== newValue) {
      if (this.gl) {
        this.updateColors(newValue);
      } else {
        this.style.background = newValue;
      }
    }
  }

  initWebGL() {
    const canvas = this.querySelector('.shader-canvas');
    if (!canvas) return false;

    const gl = canvas.getContext('webgl');
    if (!gl) return false;

    this.gl = gl;

    const vsSource = `
      attribute vec2 position;
      void main() {
        gl_Position = vec4(position, 0.0, 1.0);
      }
    `;

    const fsSource = `
      precision mediump float;
      uniform vec2 u_resolution;
      uniform float u_time;
      uniform vec3 u_color1;
      uniform vec3 u_color2;
      uniform vec3 u_color3;
      uniform vec2 u_mouse;

      float hash(vec2 p) { 
        return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123); 
      }
      float noise(vec2 p) {
        vec2 i = floor(p);
        vec2 f = fract(p);
        vec2 u = f*f*(3.0-2.0*f);
        return mix(mix(hash(i + vec2(0.0,0.0)), hash(i + vec2(1.0,0.0)), u.x),
                   mix(hash(i + vec2(0.0,1.0)), hash(i + vec2(1.0,1.0)), u.x), u.y);
      }

      void main() {
        vec2 uv = gl_FragCoord.xy / u_resolution.xy;
        float t = u_time * 0.5;
        
        // Warp coordinates system based on u_mouse to pull linear waves toward the cursor
        vec2 warpedUv = uv - (u_mouse - 0.5) * 0.6;
        
        // Diagonal coordinate using the warped grid
        float diagonal = warpedUv.x + warpedUv.y;
        
        // Organic noise on warped coordinates
        float n1 = noise(warpedUv * 3.0 + vec2(t * 0.1, -t * 0.15));
        float n2 = noise(warpedUv * 4.0 - vec2(t * 0.15, t * 0.1));
        
        // Waves moving along the diagonal axis with warped wave fronts
        float w1 = sin(diagonal * 2.5 - t * 1.5 + n1 * 2.0) * 0.5 + 0.5;
        float w2 = cos(diagonal * 2.0 - t * 1.0 + n2 * 1.5) * 0.5 + 0.5;
        
        float mixVal = (w1 + w2) * 0.5;
        
        vec3 color = mix(u_color1, u_color2, mixVal);
        color = mix(color, u_color3, w2 * w1);

        float grain = fract(sin(dot(gl_FragCoord.xy, vec2(12.9898, 78.233))) * 43758.5453) * 0.07;
        color += vec3(grain);
        
        
        gl_FragColor = vec4(color, 1.0);
      }
    `;

    const compileShader = (source, type) => {
      const shader = gl.createShader(type);
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) return null;
      return shader;
    };

    const vs = compileShader(vsSource, gl.VERTEX_SHADER);
    const fs = compileShader(fsSource, gl.FRAGMENT_SHADER);
    if (!vs || !fs) return false;

    const program = gl.createProgram();
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return false;

    this.shaderProgram = program;
    gl.useProgram(program);

    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
      -1.0, -1.0,  1.0, -1.0, -1.0,  1.0,
      -1.0,  1.0,  1.0, -1.0,  1.0,  1.0
    ]), gl.STATIC_DRAW);

    const posAttrib = gl.getAttribLocation(program, 'position');
    gl.enableVertexAttribArray(posAttrib);
    gl.vertexAttribPointer(posAttrib, 2, gl.FLOAT, false, 0, 0);

    this.uniforms = {
      resolution: gl.getUniformLocation(program, 'u_resolution'),
      time: gl.getUniformLocation(program, 'u_time'),
      color1: gl.getUniformLocation(program, 'u_color1'),
      color2: gl.getUniformLocation(program, 'u_color2'),
      color3: gl.getUniformLocation(program, 'u_color3'),
      mouse: gl.getUniformLocation(program, 'u_mouse')
    };

    this.shaderColors = {
      c1: { r: 128/255, g: 155/255, b: 214/255 },
      c2: { r: 145/255, g: 10/255, b: 255/255 },
      c3: { r: 175/255, g: 56/255, b: 255/255 }
    };

    this.mousePos = { x: 0.5, y: 0.5 };
    this.isMouseOver = false;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!this.reducedMotion) {
      if (typeof gsap !== 'undefined') {
        this.xTo = gsap.quickTo(this.mousePos, "x", { duration: 0.8, ease: "power3.out" });
        this.yTo = gsap.quickTo(this.mousePos, "y", { duration: 0.8, ease: "power3.out" });
      }

      // Fully Decoupled & Reusable: Bind mousemove to window to bypass pointer-events restrictions
      this.mousemoveHandler = (e) => {
        const rect = canvas.getBoundingClientRect();
        const x = e.clientX;
        const y = e.clientY;

        const isInside = x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;

        if (isInside) {
          this.isMouseOver = true;
          const targetX = (x - rect.left) / rect.width;
          const targetY = 1.0 - (y - rect.top) / rect.height;

          if (this.xTo && this.yTo) {
            this.xTo(targetX);
            this.yTo(targetY);
          } else {
            this.mousePos.x = targetX;
            this.mousePos.y = targetY;
          }
        } else {
          if (this.isMouseOver) {
            this.isMouseOver = false;
            if (typeof gsap !== 'undefined') {
              gsap.to(this.mousePos, {
                x: 0.5,
                y: 0.5,
                duration: 1.0,
                ease: "power2.inOut"
              });
            }
          }
        }
      };

      window.addEventListener('mousemove', this.mousemoveHandler);
    }

    this.updateColors(this.getAttribute('gradient-color'));

    this.startTime = Date.now();

    this.drawFrame = () => {
      if (!this.gl) return;
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
        gl.viewport(0, 0, width, height);
      }
      gl.useProgram(this.shaderProgram);

      if (!this.reducedMotion && !this.isMouseOver) {
        const t = (Date.now() - this.startTime) / 1000.0;
        if (typeof gsap === 'undefined' || !gsap.isTweening(this.mousePos)) {
          this.mousePos.x = 0.5 + 0.15 * Math.sin(t * 0.5);
          this.mousePos.y = 0.5 + 0.15 * Math.cos(t * 0.5);
        }
      }

      gl.uniform2f(this.uniforms.resolution, canvas.width, canvas.height);
      gl.uniform1f(this.uniforms.time, this.reducedMotion ? 0 : (Date.now() - this.startTime) / 1000.0);
      gl.uniform3f(this.uniforms.color1, this.shaderColors.c1.r, this.shaderColors.c1.g, this.shaderColors.c1.b);
      gl.uniform3f(this.uniforms.color2, this.shaderColors.c2.r, this.shaderColors.c2.g, this.shaderColors.c2.b);
      gl.uniform3f(this.uniforms.color3, this.shaderColors.c3.r, this.shaderColors.c3.g, this.shaderColors.c3.b);
      gl.uniform2f(this.uniforms.mouse, this.mousePos.x, this.mousePos.y);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
    };

    this.renderLoop = () => {
      if (!this.gl) return;
      this.drawFrame();
      this.renderFrameId = requestAnimationFrame(this.renderLoop);
    };

    if (this.renderFrameId) cancelAnimationFrame(this.renderFrameId);

    if (this.reducedMotion) {
      this.drawFrame();
    } else {
      this.renderFrameId = requestAnimationFrame(this.renderLoop);
    }
    return true;
  }

  updateColors(colorsStr) {
    if (!colorsStr) return;
    const colors = colorsStr.match(/(#[0-9a-fA-F]{3,8}|rgba?\([^)]+\)|hsla?\([^)]+\))/g) || ['#809bd6', '#910aff', '#af38ff'];

    const parseColorToRgb = (colorStr) => {
      const defaultColor = { r: 0.5, g: 0.5, b: 0.5 };
      if (!colorStr) return defaultColor;
      colorStr = colorStr.trim().toLowerCase();
      if (colorStr.startsWith('#')) {
        let c = colorStr.substring(1);
        if (c.length === 3) c = c.split('').map(x => x + x).join('');
        const num = parseInt(c, 16);
        if (isNaN(num)) return defaultColor;
        return { r: ((num >> 16) & 255) / 255, g: ((num >> 8) & 255) / 255, b: (num & 255) / 255 };
      }
      const rgbMatch = colorStr.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/);
      if (rgbMatch) {
        return { r: parseInt(rgbMatch[1], 10) / 255, g: parseInt(rgbMatch[2], 10) / 255, b: parseInt(rgbMatch[3], 10) / 255 };
      }
      return defaultColor;
    };

    const c1 = parseColorToRgb(colors[0]);
    const c2 = parseColorToRgb(colors[1] || colors[0]);
    const c3 = parseColorToRgb(colors[2] || colors[1] || colors[0]);

    if (!this.reducedMotion && typeof gsap !== 'undefined' && this.shaderColors) {
      gsap.to(this.shaderColors.c1, { r: c1.r, g: c1.g, b: c1.b, duration: 1.5, ease: 'power2.out' });
      gsap.to(this.shaderColors.c2, { r: c2.r, g: c2.g, b: c2.b, duration: 1.5, ease: 'power2.out' });
      gsap.to(this.shaderColors.c3, { r: c3.r, g: c3.g, b: c3.b, duration: 1.5, ease: 'power2.out' });
    } else if (this.shaderColors) {
      this.shaderColors.c1 = c1;
      this.shaderColors.c2 = c2;
      this.shaderColors.c3 = c3;
      if (this.reducedMotion && this.drawFrame) this.drawFrame();
    }
  }

  disconnectedCallback() {
    if (this.renderFrameId) cancelAnimationFrame(this.renderFrameId);
    if (this.mousemoveHandler) {
      window.removeEventListener('mousemove', this.mousemoveHandler);
    }
    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect();
      this.visibilityObserver = null;
    }
    this.gl = null;
  }
}
if (!customElements.get('shader-gradient-bg')) {
  customElements.define('shader-gradient-bg', ShaderGradientBg);
}